import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LanguageRouter — auto-redirects the visitor to the right language
 * version of the pitch deck. Order of precedence:
 *   1. Saved manual preference in localStorage (always wins).
 *   2. Visitor's IP country (Cloudflare CF-IPCountry via /api/geo/lang):
 *      Italy/San Marino/Vatican -> /it, everyone else -> /en.
 *   3. Browser language (instant fallback if the country can't be resolved).
 *
 * The manual switcher in the navbar saves the choice in localStorage and is
 * reused on future visits.
 */
export default function LanguageRouter() {
  const navigate = useNavigate();

  useEffect(() => {
    // 1) honour the user's previous explicit choice
    let saved = null;
    try {
      saved = localStorage.getItem("preferred_lang");
    } catch (e) {
      /* private mode */
    }
    if (saved === "it" || saved === "en") {
      navigate(`/${saved}`, { replace: true });
      return;
    }

    // browser-language fallback target
    const langs = (navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || "en"]
    ).map((l) => l.toLowerCase());
    let browserTarget = "en";
    for (const l of langs) {
      if (l.startsWith("it")) { browserTarget = "it"; break; }
      if (l.startsWith("en")) { browserTarget = "en"; break; }
    }

    // 2) IP-country detection (with a safety timeout so we never hang on /loading)
    let done = false;
    const go = (t) => {
      if (done) return;
      done = true;
      navigate(`/${t}`, { replace: true });
    };
    const timer = setTimeout(() => go(browserTarget), 2500);

    fetch(`${BACKEND_URL}/api/geo/lang`)
      .then((r) => r.json())
      .then((d) => {
        clearTimeout(timer);
        if (d && d.country && (d.lang === "it" || d.lang === "en")) go(d.lang);
        else go(browserTarget); // 3) country unknown -> browser language
      })
      .catch(() => {
        clearTimeout(timer);
        go(browserTarget);
      });
  }, [navigate]);

  return (
    <div
      data-testid="language-router-loading"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0A1628",
        color: "#F7F2E8",
        fontFamily: "Georgia, serif",
        letterSpacing: "0.15em",
        fontSize: "13px",
        textTransform: "uppercase",
      }}
    >
      <span>Mediterranean Dream · loading…</span>
    </div>
  );
}
